import { WORKSPACE_ZOOM_MAX, WORKSPACE_ZOOM_MIN } from '@/components/canvas/shared/constants/legacyConfig';
import { getPageWorldBounds } from '@/components/canvas/page/pageBounds';
import {
  computeFitViewport,
  resolveWorkspaceFitInsets,
  type WorkspaceChromeInsetsMode,
  type WorkspaceFitInsets,
} from '@/components/canvas/workspace/viewport/workspaceFit';
import type { SliceDeps } from '../deps';

export type FitToScreenOptions = {
  /** 全屏贴边：安全区内不再额外缩放留白 */
  noMargin?: boolean;
  /** @deprecated 仅保留兼容；始终适应当前激活页 */
  mode?: 'page';
  /** 覆盖默认的工具条留白 */
  insets?: WorkspaceFitInsets;
  /** 按场景选择编辑/全屏/无留白 */
  chromeInsets?: WorkspaceChromeInsetsMode;
  /** 适应 Page 内容区时排除页头；演示态使用 */
  includePageChrome?: boolean;
  /** 演示态可突破编辑器手动缩放上限，保证页面宽或高贴满屏幕 */
  maxZoom?: number;
};

function resolveFitParams(options?: FitToScreenOptions): {
  marginFactor: number;
  insets: WorkspaceFitInsets;
} {
  const chromeInsets: WorkspaceChromeInsetsMode =
    options?.chromeInsets ?? (options?.noMargin ? 'fullscreen' : 'edit');
  return {
    marginFactor: options?.noMargin ? 1 : 0.96,
    insets: options?.insets ?? resolveWorkspaceFitInsets(chromeInsets),
  };
}

export function createViewportSlice({ set, get }: SliceDeps) {
  return {
    setZoom: (newZoom: number) =>
      set({ zoom: Math.max(WORKSPACE_ZOOM_MIN, Math.min(WORKSPACE_ZOOM_MAX, newZoom)) }),

    setPan: (x: number, y: number) => set({ panX: x, panY: y }),

    fitToScreen: (
      containerWidth: number,
      containerHeight: number,
      options?: FitToScreenOptions,
    ) => {
      const { marginFactor, insets } = resolveFitParams(options);

      const page = get().pages.find((c) => c.id === get().currentPageId);
      if (!page) return;
      const bounds = getPageWorldBounds(page, get().zoom, {
        includeChrome: options?.includePageChrome,
      });
      const viewport = computeFitViewport({
        containerWidth,
        containerHeight,
        bounds,
        marginFactor,
        insets,
        maxZoom: options?.maxZoom,
      });
      set(viewport);
    },
  };
}
